import type {
  AttachDocumentInput,
  ClubInvoiceDto,
  CreateInvoiceInput,
  DocumentDto,
  InvoiceDetailDto,
  InvoiceDto,
  IssueInvoiceInput,
  NextInvoiceNumberDto,
  PdfExportQueuedDto,
  PdfExportStatusDto,
  SaveInvoiceLinesInput,
  SignedUrlDto,
  UpdateInvoiceInput,
  UploadTargetDto,
} from "@tc/shared";
import { ApiError } from "@/features/trips/api";
import { anchorNewTab, openFileTab } from "@/lib/download";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/**
 * Trip records calls (TW-3450) — invoices and documents on the trip Documents tab. Reads are shared
 * (RLS scopes each caller, and the invoice Draft-hiding predicate keeps Drafts off the club entirely);
 * the lifecycle + upload writes are back-office only, enforced server-side. The document upload is the
 * same signed-URL dance as the passenger document-image (sign → PUT bytes → attach key).
 */
export const recordsApi = {
  // ── Invoices ────────────────────────────────────────────────────────────────
  listInvoices: (tripId: string) => req<InvoiceDto[]>(`/trips/${tripId}/invoices`),
  /** One invoice with everything the factura page/PDF need (lines + issuer + client + meta, TW-3527). */
  getInvoiceDetail: (tripId: string, invoiceId: string) =>
    req<InvoiceDetailDto>(`/trips/${tripId}/invoices/${invoiceId}`),
  /** Replace a Draft invoice's line items (back-office only; re-prices the invoice). */
  saveInvoiceLines: (tripId: string, invoiceId: string, body: SaveInvoiceLinesInput) =>
    req<InvoiceDetailDto>(`/trips/${tripId}/invoices/${invoiceId}/lines`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** The caller's club-wide invoices (records list, TW-3452). BO may scope with `clubId`. */
  listClubInvoices: (clubId?: string) =>
    req<ClubInvoiceDto[]>(`/invoices${clubId ? `?clubId=${encodeURIComponent(clubId)}` : ""}`),
  createInvoice: (tripId: string, body: CreateInvoiceInput) =>
    req<InvoiceDto>(`/trips/${tripId}/invoices`, { method: "POST", body: JSON.stringify(body) }),
  updateInvoice: (tripId: string, invoiceId: string, body: UpdateInvoiceInput) =>
    req<InvoiceDto>(`/trips/${tripId}/invoices/${invoiceId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteInvoice: (tripId: string, invoiceId: string) =>
    req<void>(`/trips/${tripId}/invoices/${invoiceId}`, { method: "DELETE" }),
  /** Issue a Draft (TRAV-2): the modal's email recipients + optional message ride in the body. */
  issueInvoice: (tripId: string, invoiceId: string, body: IssueInvoiceInput = {}) =>
    req<InvoiceDto>(`/trips/${tripId}/invoices/${invoiceId}/issue`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  payInvoice: (tripId: string, invoiceId: string) =>
    req<InvoiceDto>(`/trips/${tripId}/invoices/${invoiceId}/pay`, { method: "POST" }),
  /** The number the next issued invoice will get (TRAV-2, BO-only informational preview). */
  nextInvoiceNumber: () => req<NextInvoiceNumberDto>(`/invoices/next-number`),

  // ── Documents ─────────────────────────────────────────────────────────────────
  listDocuments: (tripId: string) => req<DocumentDto[]>(`/trips/${tripId}/documents`),
  signUpload: (tripId: string) =>
    req<UploadTargetDto>(`/trips/${tripId}/documents/upload-url`, { method: "POST" }),
  attachDocument: (tripId: string, body: AttachDocumentInput) =>
    req<DocumentDto>(`/trips/${tripId}/documents`, { method: "POST", body: JSON.stringify(body) }),
  documentUrl: (tripId: string, documentId: string) =>
    req<SignedUrlDto>(`/trips/${tripId}/documents/${documentId}/download`),
  deleteDocument: (tripId: string, documentId: string) =>
    req<void>(`/trips/${tripId}/documents/${documentId}`, { method: "DELETE" }),

  // ── Branded record PDFs (TW-3451) — enqueue + poll, like the manifest export ────
  requestProposalPdf: (tripId: string) =>
    req<PdfExportQueuedDto>(`/trips/${tripId}/proposal/pdf`, { method: "POST" }),
  proposalPdfStatus: (tripId: string, exportId: string) =>
    req<PdfExportStatusDto>(`/trips/${tripId}/proposal/pdf/${exportId}`),
  requestInvoicePdf: (tripId: string, invoiceId: string) =>
    req<PdfExportQueuedDto>(`/trips/${tripId}/invoices/${invoiceId}/pdf`, { method: "POST" }),
  invoicePdfStatus: (tripId: string, invoiceId: string, exportId: string) =>
    req<PdfExportStatusDto>(`/trips/${tripId}/invoices/${invoiceId}/pdf/${exportId}`),

  /** PUT the raw file bytes straight to the signed upload URL (bypasses this API's JSON body). */
  async uploadBytes(target: UploadTargetDto, file: File): Promise<void> {
    const res = await fetch(target.url, {
      method: target.method,
      headers: { "content-type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!res.ok) throw new ApiError(`Upload failed (${res.status})`, res.status);
  },
};

/** Upload a document end-to-end: sign → PUT bytes → attach, returning the new document row. */
export async function uploadTripDocument(tripId: string, file: File): Promise<DocumentDto> {
  const target = await recordsApi.signUpload(tripId);
  await recordsApi.uploadBytes(target, file);
  return recordsApi.attachDocument(tripId, {
    key: target.key,
    filename: file.name,
    contentType: file.type || "application/octet-stream",
    sizeBytes: file.size,
  });
}

/** Resolve a signed URL for a stored document and open it in a new tab (UAT round 3). */
export async function downloadDocument(tripId: string, doc: DocumentDto): Promise<void> {
  const tab = openFileTab(); // synchronously, inside the click — see lib/download.ts
  try {
    const { url } = await recordsApi.documentUrl(tripId, doc.id);
    if (tab) tab.deliver(url);
    else anchorNewTab(url);
  } catch (err) {
    tab?.fail();
    throw err;
  }
}

/**
 * Run a branded-PDF export: enqueue it, poll the worker-written document into existence, then open
 * the signed URL in the tab pre-opened at click time (UAT round 3 — a same-tab anchor navigated the
 * app to the file, and a tab opened only on completion gets popup-blocked after the poll). Mirrors
 * `runManifestExport` (TW-3449). NOTE: in local dev the worker is a separate container with
 * per-process in-memory storage, so the poll can stay pending until the real Spaces transport
 * (TW-3459) lands; the flow is proven end to end in the API e2e (in-process).
 */
export async function runRecordPdf(
  request: () => Promise<PdfExportQueuedDto>,
  status: (exportId: string) => Promise<PdfExportStatusDto>,
): Promise<void> {
  const tab = openFileTab(); // synchronously, inside the click — see lib/download.ts
  try {
    const { exportId } = await request();
    for (let attempt = 0; attempt < 30; attempt++) {
      const s = await status(exportId);
      if (s.status === "ready") {
        if (tab) tab.deliver(s.downloadUrl);
        else anchorNewTab(s.downloadUrl);
        return;
      }
      await new Promise((r) => setTimeout(r, 600));
    }
    throw new ApiError("PDF generation timed out", 504);
  } catch (err) {
    tab?.fail();
    throw err;
  }
}
